import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { HiPlus, HiPencil, HiTrash, HiXMark, HiExclamationTriangle, HiAdjustmentsHorizontal } from 'react-icons/hi2';
import { getProducts, createProduct, updateProduct, deleteProduct, updateStock } from '../../services/api';

const TYPE_CLASS = { can: 'badge-blue', bottle: 'badge-green', jar: 'badge-purple', other: 'badge-gray' };
const fmt = (n) => new Intl.NumberFormat('en-IN').format(Number(n) || 0);
const LOW_STOCK_THRESHOLD = 20;

const EMPTY_PRODUCT = {
  name: '', type: 'jar', description: '', price: '', deposit: '',
  stock_quantity: '', unit: '', capacity: '',
};

export default function Inventory() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const [productModal, setProductModal] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [form, setForm] = useState(EMPTY_PRODUCT);
  const [saving, setSaving] = useState(false);

  const [stockModal, setStockModal] = useState(null);
  const [stockForm, setStockForm] = useState({ type: 'stock_in', quantity: '', notes: '' });
  const [savingStock, setSavingStock] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchProducts = useCallback(() => {
    setLoading(true);
    getProducts()
      .then((res) => {
        const data = res.data;
        setProducts(Array.isArray(data) ? data : data.products || []);
      })
      .catch(() => toast.error('Failed to load products.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const lowStockItems = products.filter((p) => (Number(p.stock_quantity) || 0) < LOW_STOCK_THRESHOLD);

  const openAdd = () => { setEditProduct(null); setForm(EMPTY_PRODUCT); setProductModal(true); };
  const openEdit = (p) => {
    setEditProduct(p);
    setForm({
      name: p.name || '', type: p.type || 'jar', description: p.description || '',
      price: p.price ?? '', deposit: p.deposit ?? '', stock_quantity: p.stock_quantity ?? '',
      unit: p.unit || '', capacity: p.capacity || '',
    });
    setProductModal(true);
  };
  const closeProductModal = () => { setProductModal(false); setEditProduct(null); };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || form.price === '') { toast.error('Name and price are required.'); return; }
    setSaving(true);
    const payload = {
      name: form.name, type: form.type, description: form.description,
      price: Number(form.price), deposit: form.deposit !== '' ? Number(form.deposit) : 0,
      stock_quantity: form.stock_quantity !== '' ? Number(form.stock_quantity) : 0,
      unit: form.unit, capacity: form.capacity,
    };
    try {
      if (editProduct) {
        await updateProduct(editProduct.id || editProduct._id, payload);
        toast.success('Product updated.');
      } else {
        await createProduct(payload);
        toast.success('Product added.');
      }
      closeProductModal();
      fetchProducts();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to save product.');
    } finally {
      setSaving(false);
    }
  };

  const openStockModal = (p) => {
    setStockModal(p);
    setStockForm({ type: 'stock_in', quantity: '', notes: '' });
  };

  const handleStockFormChange = (e) => {
    const { name, value } = e.target;
    setStockForm((f) => ({ ...f, [name]: value }));
  };

  const handleStockUpdate = async (e) => {
    e.preventDefault();
    if (!stockForm.quantity || Number(stockForm.quantity) <= 0) { toast.error('Enter a valid quantity.'); return; }
    setSavingStock(true);
    try {
      await updateStock(stockModal.id || stockModal._id, {
        type: stockForm.type,
        quantity: Number(stockForm.quantity),
        notes: stockForm.notes || undefined,
      });
      toast.success('Stock updated.');
      setStockModal(null);
      fetchProducts();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to update stock.');
    } finally {
      setSavingStock(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteProduct(deleteTarget.id || deleteTarget._id);
      toast.success('Product deleted.');
      setDeleteTarget(null);
      fetchProducts();
    } catch {
      toast.error('Failed to delete product.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="content-area">
      <div className="page-header">
        <h1 className="page-title">Inventory</h1>
        <button className="btn btn-primary" onClick={openAdd}><HiPlus /> Add Product</button>
      </div>

      {/* Low stock alert */}
      {lowStockItems.length > 0 && (
        <div className="alert alert-warning" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
          <HiExclamationTriangle style={{ flexShrink: 0 }} />
          <span>
            <strong>{lowStockItems.length} product{lowStockItems.length > 1 ? 's' : ''}</strong> with low stock (below {LOW_STOCK_THRESHOLD}):&nbsp;
            {lowStockItems.map((p) => p.name).join(', ')}
          </span>
        </div>
      )}

      <div className="card">
        {loading ? (
          <div className="loading-center"><div className="spinner" /></div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Capacity</th>
                  <th>Price Rs.</th>
                  <th>Deposit Rs.</th>
                  <th>Stock</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.length === 0 ? (
                  <tr><td colSpan={8} style={{ textAlign: 'center', color: '#94a3b8' }}>No products found.</td></tr>
                ) : (
                  products.map((p) => {
                    const stock = Number(p.stock_quantity) || 0;
                    const isLow = stock < LOW_STOCK_THRESHOLD;
                    return (
                      <tr key={p.id || p._id}>
                        <td style={{ fontWeight: 500 }}>{p.name}</td>
                        <td><span className={`badge ${TYPE_CLASS[p.type] || 'badge-gray'}`}>{p.type}</span></td>
                        <td>{p.capacity || '-'}</td>
                        <td>Rs.{fmt(p.price)}</td>
                        <td>Rs.{fmt(p.deposit || 0)}</td>
                        <td style={{ fontWeight: 600, color: isLow ? '#ef4444' : 'inherit' }}>
                          {stock}
                          {isLow && <HiExclamationTriangle style={{ marginLeft: '0.3rem', color: '#ef4444', verticalAlign: 'middle' }} />}
                        </td>
                        <td>
                          <span className={`badge ${p.status === 'active' || p.is_active !== false ? 'badge-green' : 'badge-gray'}`}>
                            {p.status || (p.is_active !== false ? 'active' : 'inactive')}
                          </span>
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                            <button className="btn btn-secondary btn-sm" onClick={() => openEdit(p)}><HiPencil /> Edit</button>
                            <button className="btn btn-secondary btn-sm" onClick={() => openStockModal(p)}>
                              <HiAdjustmentsHorizontal /> Stock
                            </button>
                            <button className="btn btn-danger btn-sm" onClick={() => setDeleteTarget(p)}><HiTrash /></button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit Product Modal */}
      {productModal && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && closeProductModal()}>
          <div className="modal" style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <span className="modal-title">{editProduct ? 'Edit Product' : 'Add Product'}</span>
              <button className="btn btn-secondary btn-sm" onClick={closeProductModal}><HiXMark /></button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="grid grid-2">
                  <div className="form-group">
                    <label className="form-label">Name *</label>
                    <input className="form-control" name="name" value={form.name} onChange={handleFormChange} required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Type</label>
                    <select className="form-control" name="type" value={form.type} onChange={handleFormChange}>
                      {['can', 'bottle', 'jar', 'other'].map((t) => (
                        <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Price (Rs.) *</label>
                    <input className="form-control" type="number" name="price" value={form.price} onChange={handleFormChange} min="0" step="0.01" required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Deposit (Rs.)</label>
                    <input className="form-control" type="number" name="deposit" value={form.deposit} onChange={handleFormChange} min="0" step="0.01" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Stock Quantity</label>
                    <input className="form-control" type="number" name="stock_quantity" value={form.stock_quantity} onChange={handleFormChange} min="0" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Unit</label>
                    <input className="form-control" name="unit" value={form.unit} onChange={handleFormChange} placeholder="e.g. liters" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Capacity</label>
                    <input className="form-control" name="capacity" value={form.capacity} onChange={handleFormChange} placeholder="e.g. 20L" />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Description</label>
                  <textarea className="form-control" name="description" value={form.description} onChange={handleFormChange} rows={2} />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeProductModal}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : editProduct ? 'Update Product' : 'Add Product'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Adjust Stock Modal */}
      {stockModal && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setStockModal(null)}>
          <div className="modal" style={{ maxWidth: '420px' }}>
            <div className="modal-header">
              <span className="modal-title">Adjust Stock - {stockModal.name}</span>
              <button className="btn btn-secondary btn-sm" onClick={() => setStockModal(null)}><HiXMark /></button>
            </div>
            <form onSubmit={handleStockUpdate}>
              <div className="modal-body">
                <p className="text-muted" style={{ marginBottom: '1rem' }}>
                  Current stock: <strong>{Number(stockModal.stock_quantity) || 0}</strong>
                </p>
                <div className="form-group">
                  <label className="form-label">Adjustment Type</label>
                  <select className="form-control" name="type" value={stockForm.type} onChange={handleStockFormChange}>
                    {[
                      { value: 'stock_in', label: 'Stock In' },
                      { value: 'stock_out', label: 'Stock Out' },
                      { value: 'return', label: 'Return' },
                      { value: 'adjustment', label: 'Adjustment' },
                    ].map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Quantity *</label>
                  <input className="form-control" type="number" name="quantity" value={stockForm.quantity} onChange={handleStockFormChange} min="1" required />
                </div>
                <div className="form-group">
                  <label className="form-label">Notes</label>
                  <textarea className="form-control" name="notes" value={stockForm.notes} onChange={handleStockFormChange} rows={2} />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setStockModal(null)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={savingStock}>
                  {savingStock ? 'Updating...' : 'Update Stock'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteTarget && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setDeleteTarget(null)}>
          <div className="modal" style={{ maxWidth: '400px' }}>
            <div className="modal-header">
              <span className="modal-title">Delete Product</span>
              <button className="btn btn-secondary btn-sm" onClick={() => setDeleteTarget(null)}><HiXMark /></button>
            </div>
            <div className="modal-body">
              <p>Delete product <strong>{deleteTarget.name}</strong>? This cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setDeleteTarget(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={handleDelete} disabled={deleting}>
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
